import { Article, DrawOutlined, VisibilityOutlined } from '@mui/icons-material';
import {
    Box,
    Chip,
    IconButton,
    Paper,
    Skeleton,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Tooltip,
    Typography,
    useMediaQuery,
    useTheme,
} from '@mui/material';

import Result from 'components/v2/common/Result';

import { ACTIONS } from 'constant';
import { B2B_AGREEMENT_TYPE_LABELS } from 'data/constants/b2bAgreement';
import useTranslate from 'hooks/useTranslate';
import { OrganizationAgreementInterface } from 'types';
import { formatLocalizedShortDate } from 'utils/formatDate';

interface Props {
    userServiceProviderId?: number;
    loading?: boolean;
    agreements: OrganizationAgreementInterface[];
    handleAction: (action: ACTIONS, agreement?: OrganizationAgreementInterface) => void;
}

const getDisplayName = (agreement: OrganizationAgreementInterface, agreementLabel: string) =>
    agreement.resource_name || `${agreementLabel} #${agreement.id}`;

type StatusResult = { label: string; color: 'success' | 'warning' | 'default' | 'error' };

const getAgreementStatus = (agreement: OrganizationAgreementInterface): StatusResult => {
    if (agreement.resource_type === 'ORG_TO_ORG_RETAINER_AGREEMENT') {
        if (agreement.party_a_signed_date && agreement.party_b_signed_date) {
            return { label: 'fullySigned', color: 'success' };
        }
        if (agreement.party_a_signed_date) {
            return { label: 'awaitingPartnerSignature', color: 'warning' };
        }
        return { label: 'draft', color: 'default' };
    }

    if (agreement.consultant_signed_date && agreement.candidate_signed_date) {
        return { label: 'signed', color: 'success' };
    }
    if (agreement.consultant_signed_date) {
        return { label: 'awaitingClientSignature', color: 'default' };
    }
    return { label: 'draft', color: 'default' };
};

const isB2B = (agreement: OrganizationAgreementInterface) =>
    agreement.resource_type === 'ORG_TO_ORG_RETAINER_AGREEMENT';

const AgreementTable = ({
    agreements,
    handleAction,
    userServiceProviderId,
    loading = false,
}: Props) => {
    const { translate } = useTranslate();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const partyLabel = (name: string | null, fallbackKey: string, signedDate: string | null) => {
        const who = name || translate(fallbackKey);
        if (!signedDate) {
            return `${who} ${translate('pending')}`;
        }
        return `${who}: ${formatLocalizedShortDate(signedDate)}`;
    };

    if (isMobile) {
        if (loading) {
            return (
                <Stack spacing={1.5}>
                    {Array.from({ length: 4 }).map((_, index) => (
                        <Paper key={`loading-mobile-${index}`} variant="outlined" sx={{ p: 2 }}>
                            <Stack spacing={1.25}>
                                <Skeleton variant="text" width="48%" height={32} />
                                <Skeleton variant="text" width="72%" />
                                <Stack direction="row" spacing={1}>
                                    <Skeleton variant="rounded" width={110} height={24} />
                                    <Skeleton variant="rounded" width={124} height={24} />
                                </Stack>
                            </Stack>
                        </Paper>
                    ))}
                </Stack>
            );
        }

        if (!agreements.length) {
            return (
                <Result
                    icon={<Article sx={{ fontSize: 32 }} />}
                    title={translate('noOrganizationAgreements')}
                    description={translate('noOrganizationAgreementsDescription')}
                />
            );
        }

        return (
            <Stack spacing={1.5}>
                {agreements.map((agreement) => {
                    const status = getAgreementStatus(agreement);
                    const showSignButton =
                        isB2B(agreement) &&
                        userServiceProviderId === agreement.client_id &&
                        !agreement.party_b_signed_date;
                    return (
                        <Paper key={agreement.id} variant="outlined" sx={{ p: 2 }}>
                            <Stack spacing={1}>
                                <Stack spacing={0.5}>
                                    <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                                        {getDisplayName(agreement, translate('agreement'))}
                                    </Typography>
                                    {isB2B(agreement) && agreement.agreement_type && (
                                        <Typography variant="caption" color="text.secondary">
                                            {translate(
                                                B2B_AGREEMENT_TYPE_LABELS[agreement.agreement_type]
                                            )}
                                        </Typography>
                                    )}
                                    <Typography variant="body2" color="text.secondary">
                                        {`${translate('created')} ${formatLocalizedShortDate(agreement.created_at)}`}
                                    </Typography>
                                </Stack>

                                <Stack
                                    direction="row"
                                    spacing={1}
                                    useFlexGap
                                    sx={{ flexWrap: 'wrap' }}
                                >
                                    <Chip
                                        size="small"
                                        label={translate(status.label)}
                                        color={status.color}
                                        variant={status.color === 'success' ? 'filled' : 'outlined'}
                                    />
                                    {isB2B(agreement) ? (
                                        <>
                                            <Chip
                                                size="small"
                                                label={partyLabel(
                                                    agreement.party_a_signatory_name,
                                                    'provider',
                                                    agreement.party_a_signed_date
                                                )}
                                                color={
                                                    agreement.party_a_signed_date
                                                        ? 'success'
                                                        : 'default'
                                                }
                                                variant={
                                                    agreement.party_a_signed_date
                                                        ? 'filled'
                                                        : 'outlined'
                                                }
                                            />
                                            <Chip
                                                size="small"
                                                label={partyLabel(
                                                    agreement.party_b_signatory_name,
                                                    'organizationPartner',
                                                    agreement.party_b_signed_date
                                                )}
                                                color={
                                                    agreement.party_b_signed_date
                                                        ? 'success'
                                                        : 'default'
                                                }
                                                variant={
                                                    agreement.party_b_signed_date
                                                        ? 'filled'
                                                        : 'outlined'
                                                }
                                            />
                                        </>
                                    ) : (
                                        <>
                                            <Chip
                                                size="small"
                                                label={partyLabel(
                                                    null,
                                                    'consultant',
                                                    agreement.consultant_signed_date
                                                )}
                                                color={
                                                    agreement.consultant_signed_date
                                                        ? 'success'
                                                        : 'default'
                                                }
                                                variant={
                                                    agreement.consultant_signed_date
                                                        ? 'filled'
                                                        : 'outlined'
                                                }
                                            />
                                            <Chip
                                                size="small"
                                                label={partyLabel(
                                                    null,
                                                    'client',
                                                    agreement.candidate_signed_date
                                                )}
                                                color={
                                                    agreement.candidate_signed_date
                                                        ? 'success'
                                                        : 'default'
                                                }
                                                variant={
                                                    agreement.candidate_signed_date
                                                        ? 'filled'
                                                        : 'outlined'
                                                }
                                            />
                                        </>
                                    )}
                                </Stack>

                                <Stack
                                    direction="row"
                                    spacing={0.5}
                                    sx={{ justifyContent: 'flex-end' }}
                                >
                                    <IconButton
                                        size="small"
                                        color="primary"
                                        onClick={() =>
                                            handleAction(ACTIONS.PREVIEW_FILE, agreement)
                                        }
                                    >
                                        <VisibilityOutlined fontSize="small" />
                                    </IconButton>

                                    {showSignButton && (
                                        <Tooltip title={translate('signAgreement')}>
                                            <Box component="span">
                                                <IconButton
                                                    size="small"
                                                    color="success"
                                                    onClick={() =>
                                                        handleAction(ACTIONS.SIGN, agreement)
                                                    }
                                                >
                                                    <DrawOutlined fontSize="small" />
                                                </IconButton>
                                            </Box>
                                        </Tooltip>
                                    )}
                                </Stack>
                            </Stack>
                        </Paper>
                    );
                })}
            </Stack>
        );
    }

    if (!loading && !agreements.length) {
        return (
            <Result
                icon={<Article sx={{ fontSize: 32 }} />}
                title={translate('noOrganizationAgreements')}
                description={translate('noOrganizationAgreementsDescription')}
            />
        );
    }

    return (
        <Paper variant="outlined" sx={{ borderRadius: 1.5, overflow: 'hidden' }}>
            <TableContainer sx={{ maxHeight: 520, overflow: 'auto' }}>
                <Table stickyHeader size="small" sx={{ minWidth: 840 }}>
                    <TableHead>
                        <TableRow>
                            {['name', 'status', 'provider', 'organizationPartner', 'actions'].map(
                                (label) => (
                                    <TableCell
                                        key={label}
                                        sx={{
                                            py: 1.25,
                                            px: 1.5,
                                            fontWeight: 700,
                                            bgcolor: 'grey.50',
                                            whiteSpace: 'nowrap',
                                        }}
                                    >
                                        {translate(label)}
                                    </TableCell>
                                )
                            )}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {loading
                            ? Array.from({ length: 6 }).map((_, index) => (
                                  <TableRow key={`loading-${index}`}>
                                      <TableCell>
                                          <Skeleton variant="text" width="58%" />
                                      </TableCell>
                                      <TableCell>
                                          <Skeleton variant="rounded" width={148} height={24} />
                                      </TableCell>
                                      <TableCell>
                                          <Skeleton variant="rounded" width={108} height={24} />
                                      </TableCell>
                                      <TableCell>
                                          <Skeleton variant="rounded" width={86} height={24} />
                                      </TableCell>
                                      <TableCell>
                                          <Skeleton variant="rounded" width={48} height={24} />
                                      </TableCell>
                                  </TableRow>
                              ))
                            : agreements.map((agreement) => {
                                  const status = getAgreementStatus(agreement);
                                  const b2b = isB2B(agreement);
                                  const showSignButton =
                                      b2b &&
                                      userServiceProviderId === agreement.client_id &&
                                      !agreement.party_b_signed_date;
                                  return (
                                      <TableRow
                                          key={agreement.id}
                                          hover
                                          sx={{
                                              '&:nth-of-type(even)': {
                                                  backgroundColor: 'rgba(148, 163, 184, 0.05)',
                                              },
                                              '& td': {
                                                  borderBottomColor: 'rgba(148, 163, 184, 0.16)',
                                              },
                                          }}
                                      >
                                          <TableCell sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>
                                              <Stack spacing={0.5}>
                                                  <Typography
                                                      variant="body2"
                                                      sx={{ fontWeight: 600 }}
                                                  >
                                                      {getDisplayName(
                                                          agreement,
                                                          translate('agreement')
                                                      )}
                                                  </Typography>
                                                  {b2b && agreement.agreement_type && (
                                                      <Typography
                                                          variant="caption"
                                                          color="text.secondary"
                                                      >
                                                          {translate(
                                                              B2B_AGREEMENT_TYPE_LABELS[
                                                                  agreement.agreement_type
                                                              ]
                                                          )}
                                                      </Typography>
                                                  )}
                                                  <Typography
                                                      variant="caption"
                                                      color="text.secondary"
                                                  >
                                                      {`${translate('created')} ${formatLocalizedShortDate(agreement.created_at)}`}
                                                  </Typography>
                                              </Stack>
                                          </TableCell>
                                          <TableCell>
                                              <Chip
                                                  size="small"
                                                  label={translate(status.label)}
                                                  color={status.color}
                                                  variant={
                                                      status.color === 'success'
                                                          ? 'filled'
                                                          : 'outlined'
                                                  }
                                              />
                                          </TableCell>
                                          <TableCell>
                                              <Stack spacing={0.25}>
                                                  {b2b && agreement.party_a_signatory_name && (
                                                      <Typography
                                                          variant="body2"
                                                          sx={{ fontWeight: 500 }}
                                                      >
                                                          {agreement.party_a_signatory_name}
                                                      </Typography>
                                                  )}
                                                  <Typography
                                                      variant="body2"
                                                      color="text.secondary"
                                                  >
                                                      {b2b
                                                          ? formatLocalizedShortDate(
                                                                agreement.party_a_signed_date
                                                            )
                                                          : formatLocalizedShortDate(
                                                                agreement.consultant_signed_date
                                                            )}
                                                  </Typography>
                                              </Stack>
                                          </TableCell>
                                          <TableCell>
                                              <Stack spacing={0.25}>
                                                  {b2b && agreement.party_b_signatory_name && (
                                                      <Typography
                                                          variant="body2"
                                                          sx={{ fontWeight: 500 }}
                                                      >
                                                          {agreement.party_b_signatory_name}
                                                      </Typography>
                                                  )}
                                                  <Typography
                                                      variant="body2"
                                                      color="text.secondary"
                                                  >
                                                      {b2b
                                                          ? formatLocalizedShortDate(
                                                                agreement.party_b_signed_date
                                                            )
                                                          : formatLocalizedShortDate(
                                                                agreement.candidate_signed_date
                                                            )}
                                                  </Typography>
                                              </Stack>
                                          </TableCell>
                                          <TableCell>
                                              <Stack direction="row" spacing={0.5}>
                                                  <Tooltip title={translate('previewAgreement')}>
                                                      <Box component="span">
                                                          <IconButton
                                                              size="small"
                                                              color="primary"
                                                              onClick={() =>
                                                                  handleAction(
                                                                      ACTIONS.PREVIEW_FILE,
                                                                      agreement
                                                                  )
                                                              }
                                                          >
                                                              <VisibilityOutlined fontSize="small" />
                                                          </IconButton>
                                                      </Box>
                                                  </Tooltip>

                                                  {showSignButton && (
                                                      <Tooltip title={translate('signAgreement')}>
                                                          <Box component="span">
                                                              <IconButton
                                                                  size="small"
                                                                  color="success"
                                                                  onClick={() =>
                                                                      handleAction(
                                                                          ACTIONS.SIGN,
                                                                          agreement
                                                                      )
                                                                  }
                                                              >
                                                                  <DrawOutlined fontSize="small" />
                                                              </IconButton>
                                                          </Box>
                                                      </Tooltip>
                                                  )}
                                              </Stack>
                                          </TableCell>
                                      </TableRow>
                                  );
                              })}
                    </TableBody>
                </Table>
            </TableContainer>
        </Paper>
    );
};

export default AgreementTable;
